import React from "react";
import type { LucideIcon } from "lucide-react";

type Benefit = { icon: LucideIcon; title: string; description: string };

type BenefitGridProps = {
  eyebrow: string;
  title: string;
  items: Benefit[];
  /** Work: white section with tinted cards. Stay: tinted section with white cards. */
  variant?: "tinted-cards" | "white-cards";
  /** Card heading size differs between pages in the design */
  headingSize?: "sm" | "md";
};

export default function BenefitGrid({
  eyebrow,
  title,
  items,
  variant = "tinted-cards",
  headingSize = "md",
}: BenefitGridProps) {
  const sectionBg = variant === "tinted-cards" ? "bg-white" : "bg-[#f5f8ff]";
  const cardBg = variant === "tinted-cards" ? "bg-[#f8fbff]" : "bg-white";
  const headingClass = headingSize === "md" ? "text-[18px] leading-[27px]" : "text-[14px] leading-5";

  return (
    <section className={`${sectionBg} py-20`}>
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
        <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          {title}
        </h2>

        <div className="grid grid-cols-1 gap-[20px] pt-12 sm:grid-cols-2 lg:grid-cols-5">
          {items.map(({ icon: Icon, title: itemTitle, description }) => (
            <div key={itemTitle} className={`rounded-2xl border-[1px] border-solid border-[#e5e7eb] ${cardBg} p-6`}>
              <div className="flex size-10 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
                <Icon className="size-5 text-[#017bef]" />
              </div>
              <h3 className={`pt-[16px] font-bold text-[#0a0a0a] ${headingClass}`}>{itemTitle}</h3>
              <p className="pt-2 text-[14px] leading-[22.75px] text-[#717182]">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
